import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Button } from "@/components/Button";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";

const DEFAULT_COLOR = "#6366f1";

function EpicsView() {
  const { projectId } = Route.useParams();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR);

  const q = useQuery({
    queryKey: queryKeys.epicsFor(projectId),
    queryFn: () => api.listEpics(projectId),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.epicsFor(projectId) });

  const create = useMutation({
    mutationFn: () => api.createEpic(projectId, { name: name.trim(), color }),
    onSuccess: () => {
      setName("");
      setColor(DEFAULT_COLOR);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteEpic(id),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: queryKeys.tasksFor(projectId) });
    },
  });

  const epics = q.data?.epics ?? [];

  return (
    <div className="proj-shell-content">
      <form
        className="ms-create"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <input
          type="color"
          className="ep-color-in"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          aria-label="Epic colour"
        />
        <input
          className="input"
          placeholder="New epic name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Epic name"
        />
        <Button
          variant="primary"
          type="submit"
          disabled={!name.trim() || create.isPending}
        >
          {create.isPending ? "Adding…" : "Add epic"}
        </Button>
      </form>

      {q.isLoading ? (
        <Skeleton count={3} height={70} />
      ) : epics.length === 0 ? (
        <EmptyState
          icon="grid"
          title="No epics yet."
          caption="Create an epic above, then assign tasks to it from the task detail."
        />
      ) : (
        <ul className="ms-list">
          {epics.map((ep) => {
            const pct =
              ep.taskCount > 0
                ? Math.round((ep.doneCount / ep.taskCount) * 100)
                : 0;
            return (
              <li key={ep.id} className="ms-card">
                <div className="ms-head">
                  <span
                    className="ep-swatch"
                    style={{ background: ep.color ?? DEFAULT_COLOR }}
                  />
                  <span className="ms-name">{ep.name}</span>
                  <button
                    type="button"
                    className="icon-btn ms-del"
                    aria-label={`Delete epic ${ep.name}`}
                    onClick={() => remove.mutate(ep.id)}
                  >
                    ✕
                  </button>
                </div>
                <div className="ms-prog-lab">
                  <span>
                    {ep.doneCount}/{ep.taskCount} tasks done
                  </span>
                  <span>{pct}%</span>
                </div>
                <div
                  className="progress ms-prog"
                  role="progressbar"
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="bar"
                    style={{
                      width: `${pct}%`,
                      background: ep.color ?? DEFAULT_COLOR,
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/epics")({
  component: EpicsView,
});
